<template>
  <AssignSamplesView
    v-bind="{ ...$attrs, ...$props }"
    :group-id="groupId"
    :sample-type="sampleType"
    :can-register="canRegister"
    :is-registering="isRegistering"
    :error="registerError"
    :has-pooled-samples="hasPooledSamples"
    @update-allow-pooled-samples="(a) => (allowPooledSamples = a)"
  >
    <!-- eslint-disable-next-line vue/v-slot-style -->
    <!-- <template #controls> </template> -->
    <template #notifications>
      <div
        v-if="registerError"
        class="notification is-danger is-fullwidth pb-2"
      >
        <span class="icon">
          <i class="fa-solid fa-bug" />
        </span>
        <span
          class="is-flex is-flex-direction-column"
          style="text-align: left; padding: 0.2rem 1rem; width: 100%"
        >
          {{ registerError }}
        </span>
      </div>
    </template>
    <template
      #body="{
        dropdownOptions,
        metaSample,
        sampleIdentityFields,
        sampleOptions,
        sampleGroups,
        studyOptions,
        openStudyPicker,
        toggleSampleModal,
      }"
    >
      <SamplesToDatasetsAssigner
        v-if="sampleAssignDatasets.length"
        :datasets-data="sampleAssignDatasets"
        :meta-sample="metaSample"
        :sample-identity-fields="sampleIdentityFields"
        :sample-options="sampleOptions"
        :sample-groups="sampleGroups"
        :allow-pooled-samples="allowPooledSamples"
        :study-options="studyOptions"
        :dropdown-options="dropdownOptions"
        :toggle-sample-modal="toggleSampleModal"
        :open-study-picker="openStudyPicker"
        @update-samples="(samples) => (samplesData = samples)"
        @update-datasets="(datasets) => (datasetsData = datasets)"
        @update-has-intracollection-duplication="
          (dup) => (hasPooledSamples = dup)
        "
        @error="(e) => (hasError = e)"
      >
        <template #sample-selector>{{ sampleTypeModel.label }}</template>
      </SamplesToDatasetsAssigner>
    </template>
    <template #footer>
      <div class="is-flex" :class="{ 'pt-4 pb-6': canRegister }">
        <BaseButton
          v-tooltip="{
            content: `
            <p>Please provide more information about your sample,</p>
            <p>
              e.g. did you associate your sample with a study ?
              Check the <b>Study</b> column
            </p>`,
            html: true,
          }"
          :icon="['fa-solid', 'fa-save']"
          label="Submit"
          variant="primary"
          :outlined="!canRegister"
          :disabled="!canRegister || isRegistering"
          :loading="isRegistering"
          @click="register"
        />
      </div>
    </template>
  </AssignSamplesView>
</template>

<script>
import { post } from '@/api'
import { mapGetters, mapState } from 'vuex'

import AssignSamplesView from '@/views/data-loader/AssignSamplesView.vue'
import SamplesToDatasetsAssigner from '@/components/data-loader/SamplesToDatasetsAssigner.vue'

export default {
  name: 'GenericAssignSamplesView',
  components: {
    AssignSamplesView,
    SamplesToDatasetsAssigner,
  },
  data() {
    return {
      samplesData: [],
      datasetsData: [],
      allowPooledSamples: false,
      hasPooledSamples: false,
      hasError: false,
      isRegistering: false,
      registerUrl: 'data_management/datasetcollections/register/',
      registerError: null,
    }
  },
  computed: {
    ...mapState('dataloader/dropbox', {
      groupId: (state) => state?.data?.group?.value?.id,
      transferWholeInputDir: (state) => state.transferWholeInputDir,
    }),
    ...mapState('dataloader/assay', {
      assayId: (state) => state.assayData?.id?.value || null,
      sampleType: 'sampleType',
    }),
    ...mapState('dataloader/collections', {
      collections: 'collections',
    }),
    ...mapGetters('auth', ['loggedInUser']),
    ...mapGetters('dataloader/assay', {
      assayData: 'flatAssayData',
      sampleTypeModel: 'sampleTypeModel',
    }),
    ...mapGetters('dataloader/dropbox', {
      files: 'payloadFiles',
      transferInputDir: 'transferInputDir',
    }),
    ...mapGetters('dataloader/datasets', ['datasetsList']),
    ...mapGetters('dataloader/collections', {
      assignSamples: 'hasAssignSamplesCollection',
    }),
    allDatasets() {
      return this.datasetsList.map((dataset) => {
        const collectionId = dataset.collectionId
        const collection = this.collections[collectionId]
        const collectionName = collection.name
        const collectionStudies = collection.studies || [] // should be whole object
        return {
          name: dataset.name,
          collection: collectionId,
          collectionName: collectionName,
          datafiles: dataset.files,
          owner: this.loggedInUser,
          studies: (collectionStudies.length === 1 && collectionStudies) || [],
        }
      })
    },
    sampleAssignDatasets() {
      return this.allDatasets
        .filter(({ collection }) => this.checkNeedSample(collection))
        .map((d) => ({
          ...d,
          studies: d.studies.length > 1 ? [] : d.studies,
        }))
    },
    noSampleDatasets() {
      return this.allDatasets
        .filter(({ collection }) => !this.checkNeedSample(collection))
        .map((dataset) => ({
          ...dataset,
          sample: null,
        }))
    },
    allDatasetsData() {
      return [...this.datasetsData, ...this.noSampleDatasets]
    },
    canRegister() {
      return this.datasetsList.length > 0 && this.hasError === false
    },
    autoRegister() {
      return this.sampleAssignDatasets.length === 0
    },
    payload() {
      const allowPooledSamples = this.allowPooledSamples
      const transferWholeInputDir = this.transferWholeInputDir
      const inputDir = this.transferInputDir
      const ownedBy = this.groupId
      const assays = (this.assayId && [this.assayData]) || []
      const collectionIds = new Set()
      const datasets = []
      this.allDatasetsData.forEach(
        ({
          name,
          collection: collectionId,
          datafiles,
          owner,
          studies,
          samples,
        }) => {
          const collection = this.collections[collectionId]
          const isRaw = collection.isRawData === true
          const dimensionsId = collection.dimensionsId
          const dimensionsValues = collection.dimensionsValues
          if (dimensionsId && dimensionsId !== 'na') {
            datafiles = datafiles.map((d, i) => ({
              ...d,
              [dimensionsId]: dimensionsValues[i],
            }))
          }
          const dataset = {
            assay: this.assayId || null,
            name,
            collection: collectionId,
            is_raw: isRaw,
            datafiles,
            samples,
            owner: owner.id,
            owned_by: ownedBy,
            studies: studies.map((s) => s?.id.value),
          }

          collectionIds.add(collectionId)
          datasets.push(dataset)
        },
      )

      const collections = Array.from(collectionIds).map((id) => {
        const collection = this.collections[id]
        return {
          id,
          name: collection.name,
          is_raw: collection.isRawData === true,
        }
      })

      const samples = this.samplesData.map((s) => ({
        ...s,
        type: this.sampleType,
      }))

      return {
        assays,
        allow_pooled_samples: allowPooledSamples,
        transfer_whole_input_dir: transferWholeInputDir,
        input_dir: inputDir,
        collections,
        datasets,
        samples,
        owned_by: ownedBy,
      }
    },
  },
  methods: {
    checkNeedSample(collectionId) {
      const collection = this.collections[collectionId]
      const isRaw = collection.isRaw === true
      const needSample = collection.assignSamples === true
      return isRaw || needSample
    },
    async register() {
      this.isRegistering = true
      try {
        const response = await post(this.registerUrl, {
          data: this.payload,
        })
        // TODO-SERVER: server returns 200 when datasets are already registered
        // instead of 201. It should return a specific status/message like
        // "already registered" so the UI can inform the user accordingly.
        if (response?.status >= 200 && response?.status < 300)
          this.handleSuccess(response.data)
        else console.error({ response })
      } catch (error) {
        this.registerError =
          error.response?.data?.message ||
          error.message ||
          'Registration failed. Please try again.'
      } finally {
        this.isRegistering = false
      }
    },
    async handleSuccess(response) {
      this.$store.commit('dataloader/main/SET_SUCCESS_DATA', response)
      this.$router.push({ name: 'importSuccess' })
    },
  },
  mounted() {
    if (this.autoRegister === true && this.canRegister) this.register()
  },
}
</script>
